import { createVar, globalStyle, style } from '@vanilla-extract/css';
import { calc } from '@vanilla-extract/css-utils';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';
import { PlumaPlainButton } from '@customerio/pluma-components/css/components';
import { fillScope, layerHover, layerPressed, shiftHoverStroke } from '@customerio/pluma-components/css/utils';

// ─── Tiptap editor styles ──────────────────────────────────────────────────────
// Replaces Pluma <TextArea> with tiptap <EditorContent>. The wrapper mimics the
// Pluma textAreaInputWrapper appearance (border, radius, padding, focus ring)
// while the .ProseMirror element replaces the native <textarea>.

export const tiptapEditorWrapper = style(
  {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    borderRadius: themeVars.radius['4'],
    border: `${themeVars.border['width-0125']} solid ${themeVars.color['scope-neutral-subtle-border']}`,
    backgroundColor: themeVars.color['surface-content'],
    paddingBottom: themeVars.space['500'],
    boxShadow: themeVars.depth['inset'],
    cursor: 'text',
    transition: 'border-color 0.15s ease',
    selectors: {
      '&:hover': {
        borderColor: shiftHoverStroke(themeVars.color['scope-neutral-subtle-border']),
      },
      '&:focus-within': {
        borderColor: themeVars.color['scope-accent-border'],
      },
    },
  },
  'tiptap-editor-wrapper',
);

export const tiptapEditorWrapperWithFiles = style(
  {
    paddingTop: themeVars.space['500'],
  },
  'tiptap-editor-wrapper-with-files',
);

export const tiptapEditor = style(
  {
    // The editor itself has no border/padding — that's on the wrapper
  },
  'tiptap-editor',
);

// Style the contenteditable ProseMirror div to match Pluma TextArea
globalStyle(`${tiptapEditor} .ProseMirror`, {
  outline: 'none',
  // 1 line min, 6 lines max — matches the previous TextArea minRows/maxRows.
  // Line height for font-size s is ~20px; use 1.5 multiplier as Pluma does.
  minHeight: '20px',
  maxHeight: '120px',
  overflowY: 'auto',
  padding: `${themeVars.space['150']} ${themeVars.space['150']}`,
  paddingBottom: 0,
  fontSize: themeVars['font-size']['sm'],
  lineHeight: '20px',
  fontFamily: themeVars['font-family']['sans'],
  color: themeVars.color['text-primary'],
  wordBreak: 'break-word',
});

// ProseMirror wraps lines in <p> tags — remove default margins
globalStyle(`${tiptapEditor} .ProseMirror p`, {
  margin: 0,
});

// Placeholder
globalStyle(`${tiptapEditor} .ProseMirror p.is-editor-empty:first-child::before`, {
  content: 'attr(data-placeholder)',
  float: 'left',
  color: themeVars.color['text-secondary'],
  pointerEvents: 'none',
  height: 0,
});

// The item's resting fill, so the hover layer mixes into the active item's pressed tone too.
const historyItemFill = createVar('history-item-fill');

export const historyItem = style(
  {
    position: 'relative',
    vars: { [historyItemFill]: themeVars.color['surface-content'] },
    backgroundColor: historyItemFill,
    borderRadius: themeVars.radius['4'],
    display: 'block',
    width: '100%',
    textAlign: 'left',

    selectors: {
      [`&:hover:not(.${PlumaPlainButton.classNames.plainButtonIsLoading})`]: {
        backgroundColor: layerHover(historyItemFill),
      },
      [`&.${PlumaPlainButton.classNames.plainButtonIsLoading}`]: {
        backgroundColor: layerHover(historyItemFill),
      },
    },
  },
  'history-item',
);

export const historyItemButton = style(
  {
    padding: `${themeVars.space['150']} ${themeVars.space['150']}`,
    display: 'block',
    width: '100%',
    textAlign: 'left',
    cursor: 'pointer',
  },
  'history-item-button',
);

export const historyItemActive = style(
  {
    vars: { [historyItemFill]: layerPressed(themeVars.color['surface-content']) },
  },
  'history-item-active',
);

export const historyItemLoading = style(
  {
    opacity: 0,
  },
  'history-item-loading',
);

export const dropdownMenuTopRight = style(
  {
    position: 'absolute',
    top: themeVars.space['100'],
    right: themeVars.space['100'],
  },
  'dropdown-menu-top-right',
);

export const recentChatItem = style(
  {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: themeVars.space['100'],
    width: '100%',
    textAlign: 'left',
    padding: `${themeVars.space['075']} ${themeVars.space['100']}`,
    marginLeft: calc.negate(themeVars.space['100']),
    marginRight: calc.negate(themeVars.space['100']),
    borderRadius: themeVars.radius['4'],
    cursor: 'pointer',
    boxSizing: 'content-box',

    selectors: {
      '&:hover': {
        backgroundColor: layerHover(),
      },
    },
  },
  'recent-chat-item',
);

export const chatActionButton = style(
  {
    borderRadius: themeVars.radius['full'],
  },
  'chat-action-button',
);

// Shared by the composer's pill triggers (Ask/Auto, model), which sit flush in the control row.
export const borderlessTrigger = style(
  {
    border: 'none',
  },
  'borderless-trigger',
);

// Widen the menu so single-line descriptions don't wrap awkwardly inside DropdownMenuItem.
export const approvalModeMenu = style(
  {
    maxWidth: '360px',
  },
  'approval-mode-menu',
);

// Auto mode reads as a yellow caution badge — at-a-glance signal that the Agent has
// live-write authority. Mirrors the Pluma yellow Label palette; the trigger stays clickable.
export const approvalModeTriggerAuto = style(
  {
    backgroundColor: fillScope('caution', 'subtle'),
    color: themeVars.color['scope-caution-text'],
    selectors: {
      '&:hover': {
        backgroundColor: layerHover(fillScope('caution', 'subtle')),
      },
    },
  },
  'approval-mode-trigger-auto',
);

export const modelMenu = style(
  {
    minWidth: '220px',
  },
  'model-menu',
);

// A settled model is shown, not offered: same footprint as the trigger it replaces, no affordance.
export const modelLockedLabel = style(
  {
    paddingInline: themeVars.space['050'],
    cursor: 'default',
  },
  'model-locked-label',
);

// Pluma menu items nowrap-truncate their label; restore wrapping for the rich two-line content.
export const approvalModeItemBody = style(
  {
    whiteSpace: 'normal',
  },
  'approval-mode-item-body',
);
